// apps/web/src/layouts/components/BreadcrumbContext.tsx
//
// Permite que páginas concretas sobreescrevam o label do último crumb com
// dados resolvidos em runtime (ex.: "PROP-XXXX · Nome do Segurado" no detail
// page de análise). Resolver estático de `handle.breadcrumb` no routes.tsx
// não tem acesso ao payload da query — esta ponte resolve isso sem precisar
// reintroduzir react-router loaders.
//
// Uso:
//   const setLast = useSetLastBreadcrumbLabel();
//   useEffect(() => {
//     setLast(`PROP-XXXX · ${customerName}`);
//     return () => setLast(null);
//   }, [customerName, setLast]);

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface BreadcrumbContextValue {
  /** Quando string, BreadcrumbHost substitui o label do último crumb por este. */
  lastLabel: string | null;
  setLastLabel: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [lastLabel, setLastLabel] = useState<string | null>(null);
  const value = useMemo(
    () => ({ lastLabel, setLastLabel: (label: string | null) => setLastLabel(label) }),
    [lastLabel],
  );
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Lê o label atual de override. Retorna null quando nenhuma página injetou. */
export function useBreadcrumbLastLabelOverride(): string | null {
  return useContext(BreadcrumbContext)?.lastLabel ?? null;
}

/**
 * Setter estável (referência preservada por render) — seguro pra usar em
 * useEffect deps array sem disparar loop. Páginas devem limpar o override
 * no cleanup do effect pra evitar leak entre rotas.
 */
export function useSetLastBreadcrumbLabel(): (label: string | null) => void {
  const ctx = useContext(BreadcrumbContext);
  return useCallback(
    (label: string | null) => {
      ctx?.setLastLabel(label);
    },
    [ctx],
  );
}
